// # Header component with animated title and navigation
import React, { useState, useEffect } from "react"; // # Add useEffect
import { Link, useNavigate } from "react-router-dom"; // # Import Link and useNavigate from react-router-dom
import {
  FaUsers,
  FaBoxOpen,
  FaFileAlt,
  FaQuestionCircle,
  FaGavel,
  FaShieldAlt,
  FaBars,
  FaTimes,
} from "react-icons/fa"; // # Import React Icons
import "./header.css";

function Header() {
  const navigate = useNavigate();

  // # States for title hover and mobile menu
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // # Add state for toggle menu
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // # Handle body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  // # Toggle mobile menu with improved handling
  const toggleMobileMenu = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // # Close menu when clicking overlay
  const handleOverlayClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
  };

  // # Handle menu item click
  const handleMenuItemClick = () => {
    setIsMobileMenuOpen(false);
  };

  // # Handle logo click
  const handleLogoClick = () => {
    setIsMobileMenuOpen(false); // # Close mobile menu when logo is clicked
    navigate("/");
  };

  // # Toggle menu handler
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // # Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest(".mn_hdr_nav-menu")) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  return (
    <header className="mn_hdr_main-header">
      <div className="mn_hdr_header-content">
        {/* # Logo and animated title section */}
        <div
          className="mn_hdr_brand-section"
          onClick={handleLogoClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ cursor: "pointer" }}
        >
          <div className="mn_hdr_logo">
            <img
              src="/icons/TEMPU_Logo.png"
              alt="TEMP-U Logo"
              className="mn_hdr_logo-img"
            />
          </div>
          <div className="mn_hdr_title">
            <span className="mn_hdr_title-short">TEMP-U</span>
            <span className="mn_hdr_title-full">
              The Egyptian Mentorship Program for USMLE
            </span>
          </div>
        </div>

        {/* # Desktop Navigation menu - Updated with hover */}
        <nav
          className="mn_hdr_nav-menu"
          onMouseEnter={() => setIsMenuOpen(true)}
          onMouseLeave={() => setIsMenuOpen(false)}
        >
          <button
            className={`mn_hdr_toggle-btn ${isMenuOpen ? "active" : ""}`}
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation menu"
          >
            <span>Explore</span>
            <FaBars className="mn_hdr_toggle-icon" />
          </button>

          <div
            className={`mn_hdr_menu-content ${isMenuOpen ? "active" : ""}`}
            aria-hidden={!isMenuOpen}
          >
            <div className="mn_hdr_menu-row" role="menu">
              {/* <Link to="/team" role="menuitem">
                <FaUsers className="mn_hdr_nav-icon" aria-hidden="true" />
                <span>Our Team</span>
              </Link> */}
              <Link to="/products" role="menuitem">
                <FaBoxOpen className="mn_hdr_nav-icon" aria-hidden="true" />
                <span>Products</span>
              </Link>
              <Link to="/samples" role="menuitem">
                <FaFileAlt className="mn_hdr_nav-icon" aria-hidden="true" />
                <span>Samples</span>
              </Link>
            </div>
            <div className="mn_hdr_menu-row" role="menu">
              <Link to="/faqs" role="menuitem">
                <FaQuestionCircle
                  className="mn_hdr_nav-icon"
                  aria-hidden="true"
                />
                <span>FAQs</span>
              </Link>
              <Link to="/terms" role="menuitem">
                <FaGavel className="mn_hdr_nav-icon" aria-hidden="true" />
                <span>Terms</span>
              </Link>
              <Link to="/privacy" role="menuitem">
                <FaShieldAlt className="mn_hdr_nav-icon" aria-hidden="true" />
                <span>Privacy</span>
              </Link>
            </div>
          </div>
        </nav>

        {/* # Desktop Authentication buttons */}
        <div className="mn_hdr_auth-buttons">
          <Link to="/signin" className="mn_hdr_btn-signin">
            Sign In
          </Link>
          <Link to="/signup" className="mn_hdr_btn-signup">
            Sign Up
          </Link>
        </div>

        {/* # Optimized Hamburger Menu */}
        <button
          className={`mn_hdr_hamburger-menu ${isMobileMenuOpen ? "active" : ""}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span className="mn_hdr_hamburger-line"></span>
          <span className="mn_hdr_hamburger-line"></span>
          <span className="mn_hdr_hamburger-line"></span>
        </button>

        {/* # Mobile Menu Overlay with improved handling */}
        {isMobileMenuOpen && (
          <div
            className={`mn_hdr_mobile-menu-overlay ${isMobileMenuOpen ? "active" : ""}`}
            onClick={handleOverlayClick}
            role="presentation"
          />
        )}

        {/* # Mobile Menu with improved structure */}
        <nav
          className={`mn_hdr_mobile-menu ${isMobileMenuOpen ? "active" : ""}`}
          onClick={(e) => e.stopPropagation()}
          role="navigation"
          aria-label="Mobile navigation"
        >
          <ul>
            {/* <li>
              <Link to="/team" onClick={handleMenuItemClick}>
                <FaUsers className="mn_hdr_nav-icon" />
                <span>Our Team</span>
              </Link>
            </li> */}
            <li>
              <Link to="/products" onClick={handleMenuItemClick}>
                <FaBoxOpen className="mn_hdr_nav-icon" />
                <span>Products</span>
              </Link>
            </li>
            <li>
              <Link to="/samples" onClick={handleMenuItemClick}>
                <FaFileAlt className="mn_hdr_nav-icon" />
                <span>Samples</span>
              </Link>
            </li>
            <li>
              <Link to="/faqs" onClick={handleMenuItemClick}>
                <FaQuestionCircle className="mn_hdr_nav-icon" />
                <span>FAQs</span>
              </Link>
            </li>
            <li>
              <Link to="/terms" onClick={handleMenuItemClick}>
                <FaGavel className="mn_hdr_nav-icon" />
                <span>Terms</span>
              </Link>
            </li>
            <li>
              <Link to="/privacy" onClick={handleMenuItemClick}>
                <FaShieldAlt className="mn_hdr_nav-icon" />
                <span>Privacy</span>
              </Link>
            </li>
          </ul>
          <div className="mn_hdr_auth-buttons">
            <Link
              to="/signin"
              className="mn_hdr_btn-signin"
              onClick={handleMenuItemClick}
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="mn_hdr_btn-signup"
              onClick={handleMenuItemClick}
            >
              Sign Up
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}

export default Header;
